import { motion } from 'framer-motion'
import { useInView } from './useInView'
import {
  MapPin,
  Calendar,
  CreditCard,
  Users,
  Smartphone,
  BarChart3,
} from 'lucide-react'

const capabilities = [
  { icon: MapPin, text: 'Wyszukiwanie obiektow w okolicy' },
  { icon: Calendar, text: 'Rezerwacja w czasie rzeczywistym' },
  { icon: CreditCard, text: 'Bezpieczne platnosci online' },
  { icon: Users, text: 'Szukanie trenerow personalnych' },
  { icon: Smartphone, text: 'Aplikacja mobilna iOS i Android' },
  { icon: BarChart3, text: 'Panel zarzadzania dla wlascicieli' },
]

export default function Product() {
  const { ref, isInView } = useInView()

  return (
    <section id="product" className="relative py-32 overflow-hidden">
      <div className="absolute inset-0 bg-radial-top" />

      <div ref={ref} className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left - product info */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7 }}
          >
            <p className="text-accent-400 font-semibold text-sm tracking-widest uppercase mb-4">
              Nasz produkt
            </p>
            <h2 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight mb-6">
              Spotto
            </h2>
            <p className="text-lg text-zinc-400 leading-relaxed mb-4">
              Platforma ktora rewolucjonizuje sposob rezerwacji obiektow
              sportowych. Znajdz idealne miejsce do uprawiania sportu, zarezerwuj
              je w kilka sekund i zarzadzaj swoimi treningami - wszystko w jednej
              aplikacji.
            </p>
            <p className="text-zinc-500 leading-relaxed mb-10">
              Dla wlascicieli obiektow Spotto to kompleksowe narzedzie do
              zarzadzania rezerwacjami, platnosci, kalendarzem dostepnosci i
              komunikacja z klientami. Transparentne ceny, brak ukrytych oplat.
            </p>

            <div className="grid sm:grid-cols-2 gap-4">
              {capabilities.map((item, i) => (
                <motion.div
                  key={item.text}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.3 + i * 0.08 }}
                  className="flex items-center gap-3 text-sm text-zinc-300"
                >
                  <div className="w-8 h-8 rounded-lg bg-accent-500/10 flex items-center justify-center shrink-0">
                    <item.icon size={16} className="text-accent-400" />
                  </div>
                  {item.text}
                </motion.div>
              ))}
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
              <a
                href="https://play.google.com/store/apps/details?id=pl.spotto.mobile"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white/[0.06] hover:bg-white/[0.1] border border-white/[0.08] rounded-xl text-sm font-semibold text-white transition-all"
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
                  <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.199l2.807 1.626a1 1 0 0 1 0 1.732l-2.807 1.626L15.206 12l2.492-2.492zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z" />
                </svg>
                Google Play
              </a>
              <a
                href="https://apps.apple.com/app/spotto"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white/[0.06] hover:bg-white/[0.1] border border-white/[0.08] rounded-xl text-sm font-semibold text-white transition-all"
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                </svg>
                App Store
              </a>
            </div>
          </motion.div>

          {/* Right - phone mockup */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="relative flex justify-center"
          >
            <div className="relative">
              {/* Glow behind phone */}
              <div className="absolute inset-0 bg-brand-600/10 rounded-[60px] blur-[80px] scale-75" />

              {/* Phone frame */}
              <div className="relative w-[280px] sm:w-[320px] rounded-[40px] bg-gradient-to-b from-zinc-700 to-zinc-800 p-[3px] shadow-2xl">
                <div className="rounded-[37px] bg-[#0c0c10] overflow-hidden">
                  {/* Status bar */}
                  <div className="h-12 bg-brand-700 flex items-center justify-center">
                    <div className="w-24 h-5 rounded-full bg-black/40" />
                  </div>

                  {/* App content mockup */}
                  <div className="px-5 py-6 space-y-5">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-brand-600/30 flex items-center justify-center">
                        <span className="text-brand-300 text-xs font-bold">S</span>
                      </div>
                      <div>
                        <div className="w-24 h-3 bg-white/20 rounded-full" />
                        <div className="w-16 h-2 bg-white/10 rounded-full mt-1.5" />
                      </div>
                    </div>

                    <div className="h-px bg-white/[0.06]" />

                    <div className="space-y-3">
                      <div className="w-32 h-3 bg-white/15 rounded-full" />
                      {[1, 2, 3].map((i) => (
                        <div
                          key={i}
                          className="p-4 rounded-2xl bg-white/[0.04] border border-white/[0.06]"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="w-28 h-3 bg-white/20 rounded-full" />
                              <div className="w-20 h-2 bg-white/10 rounded-full mt-2" />
                            </div>
                            <div className="w-16 h-8 rounded-lg bg-brand-600/30" />
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="flex justify-around pt-4 border-t border-white/[0.06]">
                      {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="flex flex-col items-center gap-1">
                          <div className="w-6 h-6 rounded-lg bg-white/10" />
                          <div className="w-8 h-1.5 bg-white/10 rounded-full" />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
